import {
	useEffect, useCallback, useRef, useState, useMemo
} from 'react';
import { connect } from 'react-redux';
import { Button, Tooltip } from '@mui/material';
import SelectPicker from 'rsuite/SelectPicker';
import Toggle from 'rsuite/Toggle';
import Input from 'rsuite/Input';
import styles from './KitThresholdListPage.module.scss';
import { notificationServices } from '../../../Services';
import kitThresholdServices from '../../../Services/kitThreshold.services';
import { BackdropLoader } from '../../../Components/Loaders';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { ListTable } from '@/Components/ListTable';
// eslint-disable-next-line import/no-unresolved, import/extensions
// eslint-disable-next-line import/no-unresolved, import/extensions
import { formActions } from '@/Actions';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { useAuth } from '@/Components/Auth/Login/auth';

const ENTITY = 'KIT_THRESHOLD';

const TABLE_COLUMNS = [
	{
		title: 'UY Variant ID',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'SkuCode',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Name',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'SKU Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Sub Category',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Unit',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Batch Size',
		config: {
			type: 'DATA',
		},
	},
];

const SPECIAL_COLUMNS = [
	{
		title: 'Kits',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Qty To Produce',
		config: {
			type: 'DATA',
		},
	},
];

// Stable identity: ListTable resets its visible-column state whenever this array
// changes, and the grid re-renders on every keystroke in a kit input.
const COLUMNS = [...TABLE_COLUMNS, ...SPECIAL_COLUMNS];

const COLMAP = {
	'UY Variant ID': 'variantId',
	SkuCode: 'clientVariantId',
	Name: 'productNameDisplay',
	'SKU Type': 'skuType',
	Category: 'category',
	'Sub Category': 'subCategoryName',
	Unit: 'unit',
	'Batch Size': 'uniqueKey',
	Kits: 'uniqueKey',
	'Qty To Produce': 'uniqueKey',
};

// Kits are only ever prepped for items that are produced. INGREDIENT, PACKAGING
// and OTHER are never planned as kits, so they are not offered here and the API
// applies the same three by default.
const KIT_SKU_TYPES = ['PRODUCT', 'UNPACKED_PRODUCT', 'INTERMEDIATE'];
const skuTypes = ['ALL', ...KIT_SKU_TYPES];

// Batch size and produced qty are stored in base units (g / ml) but read as
// Kg / Ltr, the same convention as the Variants Threshold grid. Kits stay a plain
// count and nothing is converted on the way to the API -- the plan does its own
// `kits * batchSize` in base units.
const WEIGHT_UNITS = ['gram', 'grams', 'gm', 'g'];
const VOLUME_UNITS = ['ml', 'milliliter', 'millilitre'];

const getUnitMeta = (unit) => {
	const normalized = (unit ?? '').toString().trim().toLowerCase();
	if (WEIGHT_UNITS.includes(normalized)) {
		return {
			convert: true, label: 'Kg',
		};
	}
	if (VOLUME_UNITS.includes(normalized)) {
		return {
			convert: true, label: 'Ltr',
		};
	}
	return {
		convert: false, label: unit,
	};
};

// Avoids float artefacts: 7500 / 1000 * 3 would otherwise show 22.500000000000004.
const roundValue = (num, precision = 3) => {
	const factor = 10 ** precision;
	return Math.round(num * factor) / factor;
};

// base (g / ml) -> display (Kg / Ltr); anything else is left alone.
const toDisplayValue = (value, unit) => {
	if (value === null || value === undefined || value === '') return value;
	const num = Number(value);
	if (Number.isNaN(num)) return value;
	return getUnitMeta(unit).convert ? roundValue(num / 1000) : num;
};

// The plan raises `kits * batchSize` for every configured variant
// (productionPlan.createKitsThresholdBased), so the screen shows the same product.
const getQtyToProduce = (kits, batchSize) => {
	const count = Number(kits);
	const size = Number(batchSize);
	if (!count || !size || Number.isNaN(count) || Number.isNaN(size)) return null;
	return count * size;
};

const generateInput = (value, onInputChange, disabled = false, title = '') => {
	return (
		<div className={styles['kit-input']}>
			<input
				className={styles.kit_input}
				type='number'
				min='0'
				step='1'
				title={title}
				disabled={disabled}
				value={value}
				onChange={onInputChange}
			/>
		</div>
	);
};

const KitThresholdListPage = (props) => {
	const {
		fetchKitThreshold,
		data,
		stores,
		fetchStoresList,
		updateKitThresholdList,
	} = props;
	const listRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedStore, setSelectedStore] = useState(null);
	const [selectedSkuType, setSelectedSkuType] = useState(null);
	// Every product of the account is a lot of rows to scroll; the toggle narrows
	// the grid to the variants that already carry a kit count.
	const [configuredOnly, setConfiguredOnly] = useState(false);
	const [updateState, setUpdateState] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const { userConfig = {} } = useAuth();
	const { level } = userConfig?.userDetails || {};
	const readOnly = level === 1;

	useEffect(() => {
		fetchStoresList({
			entity: 'STORES',
			req: {
				params: {
					id: '',
				},
			},
			method: 'LIST',
		});
		return () => {
			updateKitThresholdList({
				entity: ENTITY,
				method: 'LIST_DATA',
				innerField: 'data',
				data: [],
			});
		};
	}, []);

	const listParams = useMemo(() => {
		return {
			storeId: selectedStore,
			// toQueryStringURL uses searchParams.append, which serialises null as the
			// literal string "null" — ALL has to drop the key, not send an empty value.
			...(selectedSkuType &&
				selectedSkuType !== 'ALL' && {
				productType: selectedSkuType,
			}),
			...(configuredOnly && {
				configuredOnly: 1,
			}),
		};
	}, [selectedStore, selectedSkuType, configuredOnly]);

	const fetchList = useCallback(() => {
		if (!selectedStore) return;
		fetchKitThreshold({
			entity: ENTITY,
			req: {
				params: listParams,
			},
			method: 'LIST_DATA',
			showNotification: true,
		});
	}, [selectedStore, listParams]);

	useEffect(() => {
		if (!selectedStore) {
			updateKitThresholdList({
				entity: ENTITY,
				method: 'LIST_DATA',
				innerField: 'data',
				data: [],
			});
			return;
		}
		updateKitThresholdList({
			entity: ENTITY,
			method: 'LIST_DATA',
			innerField: 'data',
			data: [],
		});
		fetchList();
	}, [selectedStore, listParams]);

	const flattenedData = useMemo(() => {
		const result = [];
		data?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				result.push({
					...product,
					...variant,
					// After both spreads: the product id is what every lookup keys on,
					// so it must win over anything an id-shaped variant field sets.
					id: product?.id,
					productNameDisplay: `${product?.productName} (${variant?.variantName})`,
					// Double underscore separator, same convention as the
					// Variants Threshold grid.
					uniqueKey: `${product?.id}__${variant?.variantId}`,
				});
			});
		});
		return result;
	}, [data]);

	const filteredData = useMemo(() => {
		const query = searchQuery?.trim()?.toLowerCase();
		if (!query) return flattenedData;
		return flattenedData.filter((item) => {
			return (
				item?.productNameDisplay?.toLowerCase()?.includes(query) ||
				`${item?.clientVariantId ?? ''}`.toLowerCase()?.includes(query)
			);
		});
	}, [flattenedData, searchQuery]);

	// A fresh list resets every pending edit — the values on screen must always
	// be the values the store actually has.
	useEffect(() => {
		const temp = {};
		data?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				if (!temp[product?.id]) temp[product?.id] = {};
				temp[product?.id][variant?.variantId] =
					variant?.threshold ?? '';
			});
		});
		setUpdateState(temp);
	}, [data]);

	const onInputChange = (val, prodId, varId) => {
		// Kits are whole units of a batch — no decimals.
		if (val !== '' && !/^\d*$/.test(val)) {
			return;
		}
		setUpdateState((prev) => {
			return {
				...prev,
				[prodId]: {
					...prev?.[prodId],
					[varId]: val,
				},
			};
		});
	};

	// Only the cells the user actually changed are sent, so a save never
	// rewrites rows that were merely on screen.
	const editValues = useMemo(() => {
		const changed = {};
		data?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				const current = updateState?.[product?.id]?.[variant?.variantId];
				const original =
					variant?.threshold === null ||
					variant?.threshold === undefined
						? ''
						: `${variant?.threshold}`;
				if (`${current ?? ''}` === original) return;
				if (!changed[product?.id]) changed[product?.id] = {};
				changed[product?.id][variant?.variantId] = current === '' ? null : Number(current);
			});
		});
		return changed;
	}, [data, updateState]);

	const hasChanges = Object.keys(editValues ?? {}).length > 0;

	const onSubmit = async () => {
		if (!selectedStore) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Select a store before saving.',
			});
			return;
		}
		if (!hasChanges) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Nothing has changed.',
			});
			return;
		}
		try {
			setIsLoading(true);
			const response = await kitThresholdServices.updateKitThreshold({
				data: {
					storeId: selectedStore,
					editValues,
				},
			});
			if (response?.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: response?.message,
				});
				fetchList();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.errorMessage || response?.message || 'Update failed',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occured while saving kit thresholds',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const findRow = (value, item) => {
		return item || flattenedData.find((ele) => ele?.uniqueKey === value);
	};

	const SPECIAL_ITEMS = {
		Unit: (unit) => {
			return getUnitMeta(unit).label ?? '-';
		},
		'Batch Size': (value, item) => {
			const rowData = findRow(value, item);
			if (!rowData?.batchSize) {
				return (
					<Tooltip title='No batch size configured for this variant at this store — a kit count here will be skipped when the plan runs.'>
						<span className={styles.missing}>Not set</span>
					</Tooltip>
				);
			}
			const { label } = getUnitMeta(rowData?.unit);
			return `${toDisplayValue(rowData?.batchSize, rowData?.unit)} ${
				label ?? ''
			}`.trim();
		},
		Kits: (value, item) => {
			const rowData = findRow(value, item);
			const { id: prodId, variantId } = rowData || {};
			const kits = updateState?.[prodId]?.[variantId] ?? '';
			return generateInput(
				kits,
				(e) => onInputChange(e.target.value, prodId, variantId),
				readOnly,
				'Number of kits to prepare — 1 kit = 1 batch quantity of this variant. Set 0 to leave it out of the plan.'
			);
		},
		'Qty To Produce': (value, item) => {
			const rowData = findRow(value, item);
			const { id: prodId, variantId, batchSize, unit } = rowData || {};
			const qty = getQtyToProduce(
				updateState?.[prodId]?.[variantId],
				batchSize
			);
			if (qty === null) return '-';
			return `${toDisplayValue(qty, unit)} ${
				getUnitMeta(unit).label ?? ''
			}`.trim();
		},
	};

	const handleExcelDownload = useCallback((header, fileData, fileName) => {
		const downloadBtn = document.createElement('a');
		const csvData = [[...header], ...fileData];
		const csv = `${csvData
			.map((e) => {
				return e.join(',');
			})
			.join('\n ')}`;
		const encodedValue = new Blob([csv], {
			type: 'text/csv',
		});
		downloadBtn.href = window.URL.createObjectURL(encodedValue);
		downloadBtn.target = '_blank';
		downloadBtn.download = fileName;
		downloadBtn.click();
	}, []);

	const handleExport = () => {
		const headers = [
			'Item Code',
			'UY Item Id',
			'Store Id',
			'Store Code',
			'Store Name',
			'Product Category',
			'SKU Type',
			'Product Name',
			'Variant Name',
			'Unit',
			'Batch Size',
			'Kits',
			'Qty To Produce',
		];
		const rows = [];
		const storeNames = new Set();
		flattenedData.forEach((item) => {
			storeNames.add(item?.storeName);
			rows.push([
				item?.clientVariantId,
				item?.variantId,
				item?.storeId,
				item?.clientStoreId,
				item?.storeName,
				item?.category,
				item?.skuType,
				item?.productName?.split(',').join(' '),
				item?.variantName,
				getUnitMeta(item?.unit).label,
				toDisplayValue(item?.batchSize, item?.unit),
				item?.threshold,
				toDisplayValue(
					getQtyToProduce(item?.threshold, item?.batchSize),
					item?.unit
				),
			]);
		});
		handleExcelDownload(
			headers,
			rows,
			`Kit Threshold for Stores ${[...storeNames].join(',')}`
		);
	};

	const BASE_STORES = useMemo(() => {
		return stores?.map((ele) => {
			return {
				...ele,
				label: ele?.name,
				role: ele?.type,
				value: ele?.id,
			};
		});
	}, [stores]);

	return (
		<div className={styles.root}>
			<BackdropLoader showProgress isLoading={isLoading} />
			<div className={styles.header}>
				<div className={styles.filter}>
					<Input
						value={searchQuery}
						onChange={(value) => {
							setSearchQuery(value);
						}}
						style={{
							width: 200,
							borderRadius: 25,
							border: '1px solid #000',
						}}
						placeholder='Search Product / SKU'
					/>
					<div className={styles['store-container']}>
						<SelectPicker
							data={BASE_STORES}
							style={{
								width: 224,
							}}
							groupBy='role'
							placeholder='Select Store'
							onChange={(storeId) => {
								setSelectedStore(storeId);
							}}
							value={selectedStore}
							cleanable={false}
							renderValue={(value, item) => {
								return (
									<div className='rsuite-select-picker-render-value'>
										{item?.label}
									</div>
								);
							}}
						/>
						{!selectedStore && <p>Select Store to get Kit thresholds</p>}
					</div>
					<SelectPicker
						data={skuTypes?.map((ele) => {
							return {
								label: ele,
								value: ele,
							};
						})}
						value={selectedSkuType}
						onChange={(skuTypeValue) => {
							setSelectedSkuType(skuTypeValue);
						}}
						cleanable={false}
						searchable={false}
						style={{
							width: 150,
						}}
						placeholder='SKU Type'
					/>
					<div className={styles['toggle-container']}>
						<Toggle
							checked={configuredOnly}
							onChange={(checked) => setConfiguredOnly(checked)}
							disabled={!selectedStore}
						/>
						<span>Configured only</span>
					</div>
				</div>
				<div className='flex flex-row gap-2'>
					{selectedStore && hasChanges && !readOnly && (
						<Button
							variant='contained'
							className={styles.button}
							onClick={onSubmit}
						>
							Submit
						</Button>
					)}
				</div>
			</div>
			<div ref={listRef} className={styles['list-container']}>
				<ListTable
					useWindowScroll={false}
					colMap={COLMAP}
					columns={COLUMNS}
					data={filteredData}
					fixedHeaderTop={0}
					listRef={listRef}
					showTotal
					actions={[]}
					special={SPECIAL_ITEMS}
					classname={styles.table}
					onExportClick={handleExport}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const data = state.form[ENTITY].data.LIST_DATA;
	const stores = state?.form?.STORES?.data?.LIST;
	return {
		data,
		stores,
	};
};

const mapDispatchToProps = {
	fetchKitThreshold: formActions.gateway,
	fetchStoresList: formActions.gateway,
	updateKitThresholdList: formActions.updateFields,
};

export default connect(mapStateToProps, mapDispatchToProps)(KitThresholdListPage);
